import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-fefo-compliance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    TableModule,
  ],
  templateUrl: './fefo-compliance.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FefoComplianceComponent implements OnInit {
  private readonly batches = inject(BatchManagementService);
  private readonly permissionService = inject(PermissionService);
  private readonly cdr = inject(ChangeDetectorRef);

  warehouseId: number | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  loading = false;
  totalEvents = 0;
  compliantCount = 0;
  violationCount = 0;
  complianceRate = 0;
  violations: any[] = [];

  get canView(): boolean {
    return this.permissionService.canDoAction('inventory.fefo_compliance.view');
  }

  ngOnInit(): void {
    const now = new Date();
    this.toDate = now;
    this.fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
    if (this.canView) {
      this.search();
    }
  }

  search(): void {
    if (!this.canView) {
      return;
    }
    this.loading = true;
    this.batches
      .fefoComplianceSummary({
        warehouseId: this.warehouseId ?? undefined,
        fromDate: this.toIsoStart(this.fromDate),
        toDate: this.toIsoEnd(this.toDate),
        violationLimit: 50,
      })
      .pipe(
        switchMap((response) =>
          BlobConverterUtil.isBlob(response)
            ? BlobConverterUtil.convertToJson(response)
            : of(response),
        ),
      )
      .subscribe({
        next: (result) => {
          this.totalEvents = result?.totalEvents ?? 0;
          this.compliantCount = result?.compliantCount ?? 0;
          this.violationCount = result?.violationCount ?? 0;
          this.complianceRate = Number(result?.complianceRate ?? 0);
          this.violations = result?.violations ?? [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.totalEvents = 0;
          this.compliantCount = 0;
          this.violationCount = 0;
          this.complianceRate = 0;
          this.violations = [];
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private toIsoStart(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private toIsoEnd(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }
}
